import { Route, Routes, Navigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { LoginPage } from "@/routes/login";
import { useAuthStore } from "@/features/auth/store";
import { DashboardPage } from "@/routes/dashboard";
import { DocumentsListPage } from "@/routes/documents/list";
import { DocumentDetailPage } from "@/routes/documents/detail";
import { DocumentRecordsPage } from "@/routes/documents/records";
import { DocumentPublishedPage } from "@/routes/documents/published";
import { DocumentLibraryPage } from "@/routes/documents/library";
import { DocumentIncomingPage } from "@/routes/documents/incoming";
import { DocumentOutgoingPage } from "@/routes/documents/outgoing";
import { DocumentOrgTreePage } from "@/routes/documents/org-tree";
import { MeetingListPage } from "@/routes/meetings/list";
import { MeetingSchedulePage } from "@/routes/meetings/schedule";
import { MeetingMinutesPage } from "@/routes/meetings/minutes";
import { WorkReportPage } from "@/routes/reports/work-report";
import { StaffRatingReportPage } from "@/routes/reports/staff-rating";
import { AdminPage } from "@/routes/admin";
import { AiConfigPage } from "@/routes/admin/ai-config";
import { AiUsagePage } from "@/routes/admin/ai-usage";
import { MissionListPage } from "@/routes/tasks/mission-list";
import { TaskDetailPage } from "@/routes/tasks/detail";
import { NotificationsPage } from "@/routes/notifications";
import { PlaceholderPage } from "@/routes/placeholder";
import { TextSignListPage } from "@/routes/signs/list";
import { TextSignDetailPage } from "@/routes/signs/detail";
import { TextSignNewPage } from "@/routes/signs/new";
import { WorkRequestsPage } from "@/routes/work/requests";
import { HqcvPage } from "@/routes/work/hqcv";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="*"
        element={
          <RequireAuth>
            <AppShell>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />

                <Route path="/documents" element={<DocumentsListPage />} />
                <Route path="/documents/incoming" element={<DocumentIncomingPage />} />
                <Route path="/documents/outgoing" element={<DocumentOutgoingPage />} />
                <Route path="/documents/org-tree" element={<DocumentOrgTreePage />} />
                <Route path="/documents/records" element={<DocumentRecordsPage />} />
                <Route path="/documents/published" element={<DocumentPublishedPage />} />
                <Route path="/documents/library" element={<DocumentLibraryPage />} />
                <Route path="/documents/:id" element={<DocumentDetailPage />} />

                <Route path="/tasks" element={<MissionListPage />} />
                <Route path="/tasks/:id" element={<TaskDetailPage />} />

                <Route path="/signs" element={<Navigate to="/signs/trinh-ky" replace />} />
                <Route path="/signs/trinh-ky" element={<TextSignListPage kind="trinh-ky" />} />
                <Route path="/signs/ky-duyet" element={<TextSignListPage kind="ky-duyet" />} />
                <Route path="/signs/ky-nhay" element={<TextSignListPage kind="ky-nhay" />} />
                <Route path="/signs/ban-hanh" element={<TextSignListPage kind="ban-hanh" />} />
                <Route path="/signs/thao-luan" element={<TextSignListPage kind="thao-luan" />} />
                <Route path="/signs/don-vi" element={<TextSignListPage kind="don-vi" />} />
                {/* legacy paths — redirect to OLD-aligned names so dashboard deep-links don't break */}
                <Route path="/signs/inbox" element={<Navigate to="/signs/ky-duyet" replace />} />
                <Route path="/signs/drafts" element={<Navigate to="/signs/trinh-ky" replace />} />
                <Route path="/signs/sent" element={<Navigate to="/signs/trinh-ky" replace />} />
                <Route
                  path="/signs/ready-publish"
                  element={<Navigate to="/signs/ban-hanh" replace />}
                />
                <Route
                  path="/signs/published"
                  element={<Navigate to="/signs/ban-hanh" replace />}
                />
                <Route path="/signs/org" element={<Navigate to="/signs/don-vi" replace />} />
                <Route path="/signs/new" element={<TextSignNewPage />} />
                <Route path="/signs/:id/edit" element={<TextSignNewPage mode="edit" />} />
                <Route path="/signs/:id" element={<TextSignDetailPage />} />

                <Route path="/meetings/personal" element={<MeetingSchedulePage />} />
                <Route path="/meetings/org" element={<MeetingListPage kind="org" />} />
                <Route path="/meetings/rooms" element={<MeetingListPage kind="rooms" />} />
                <Route path="/meetings/minutes" element={<MeetingMinutesPage />} />

                <Route path="/work/requests" element={<WorkRequestsPage />} />
                <Route
                  path="/work/summary"
                  element={
                    <WorkReportPage
                      mode="summary"
                      title="Tổng hợp công việc"
                      breadcrumb="Công việc › Tổng hợp"
                    />
                  }
                />
                <Route
                  path="/work/dept"
                  element={
                    <WorkReportPage
                      mode="summary"
                      scope="dept"
                      title="Công việc Phòng ban"
                      breadcrumb="Công việc › Phòng ban"
                    />
                  }
                />
                <Route path="/work/self-review" element={<HqcvPage mode="self" />} />
                <Route path="/work/reviews" element={<HqcvPage mode="manage" />} />

                <Route path="/notifications" element={<NotificationsPage />} />

                <Route
                  path="/reports/bc01"
                  element={
                    <WorkReportPage
                      mode="perform"
                      title="Thực hiện công việc – BC01"
                      breadcrumb="Báo cáo › BC01"
                    />
                  }
                />
                <Route
                  path="/reports/bc11"
                  element={
                    <WorkReportPage
                      mode="plan"
                      title="Kế hoạch công việc – BC11"
                      breadcrumb="Báo cáo › BC11"
                    />
                  }
                />
                <Route path="/reports/staff" element={<StaffRatingReportPage />} />

                <Route path="/admin/users" element={<AdminPage kind="users" />} />
                <Route path="/admin/roles" element={<AdminPage kind="roles" />} />
                <Route path="/admin/orgs" element={<AdminPage kind="orgs" />} />
                <Route path="/admin/groups" element={<AdminPage kind="groups" />} />
                <Route path="/admin/sign-flows" element={<AdminPage kind="sign-flows" />} />
                <Route path="/admin/activity-log" element={<AdminPage kind="activity-log" />} />
                <Route path="/admin/ai-config" element={<AiConfigPage />} />
                <Route path="/admin/ai-usage" element={<AiUsagePage />} />

                <Route
                  path="*"
                  element={
                    <PlaceholderPage
                      title="Không tìm thấy"
                      breadcrumb="404"
                      note="Đường dẫn này chưa tồn tại hoặc đã bị xoá."
                    />
                  }
                />
              </Routes>
            </AppShell>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
